"use client";

import React from "react";

import { Checkbox } from "./Checkbox";

export interface CategoryNode {
  name: string;
  path: string;
  children?: CategoryNode[];
  count?: number;
}

export interface CategoryFilterProps {
  label?: string;
  categories: CategoryNode[];
  navigationStack: CategoryNode[];
  onNavigationChange: (stack: CategoryNode[]) => void;
  onCategoryNavigate: (path: string) => void;
  onRootNavigate?: () => void;
  isHierarchyNavigationEnabled?: boolean;
  currentPath?: string;
  selectedLeafPaths?: string[];
  onLeafToggle?: (path: string) => void;
  className?: string;
}

function ChevronDown({ isExpanded }: { isExpanded: boolean }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden="true"
      style={{ transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)" }}
    >
      <path d="M3 5L7 9L11 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function ChevronBack() {
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
      <path d="M6.5 2L3.5 5L6.5 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

export function CategoryFilter({
  label = "CATEGORY",
  categories,
  navigationStack,
  onNavigationChange,
  onCategoryNavigate,
  onRootNavigate,
  isHierarchyNavigationEnabled = true,
  currentPath,
  selectedLeafPaths = [],
  onLeafToggle,
  className = "",
}: CategoryFilterProps) {
  const [isExpanded, setIsExpanded] = React.useState(true);
  const currentParent =
    navigationStack.length > 0 ? navigationStack[navigationStack.length - 1] : null;

  const handleNonLeafClick = (node: CategoryNode) => {
    if (!isHierarchyNavigationEnabled) return;
    const newStack = [...navigationStack, node];
    onNavigationChange(newStack);
    onCategoryNavigate(node.path);
  };

  const handleBackClick = () => {
    if (!isHierarchyNavigationEnabled || navigationStack.length === 0) return;
    const newStack = navigationStack.slice(0, -1);
    onNavigationChange(newStack);
    const parentNode = newStack.length > 0 ? newStack[newStack.length - 1] : null;
    if (parentNode) {
      onCategoryNavigate(parentNode.path);
    } else if (onRootNavigate) {
      onRootNavigate();
    } else {
      const baseCategory = categories[0];
      const pathSegment = baseCategory?.path.split(" > ")[0];
      if (pathSegment) onCategoryNavigate(pathSegment);
    }
  };

  const handleLeafClick = (node: CategoryNode) => {
    onLeafToggle?.(node.path);
  };

  return (
    <div className={className}>
      <div
        style={{
          width: "100%",
          height: "41.974px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <span
          style={{
            fontSize: "10.5px",
            lineHeight: "14px",
            letterSpacing: "0.6173px",
            textTransform: "uppercase",
            color: "#6A7282",
            fontWeight: 500,
          }}
        >
          {label}
        </span>
        <button
          onClick={() => setIsExpanded((prev) => !prev)}
          style={{
            width: "14px",
            height: "14px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            background: "none",
            border: "none",
            padding: 0,
          }}
        >
          <ChevronDown isExpanded={isExpanded} />
        </button>
      </div>

      {isExpanded && (
        <div
          style={{
            paddingBottom: "17px",
            paddingLeft: "0px",
            paddingRight: "15px",
            paddingTop: "0px",
            display: "flex",
            flexDirection: "column",
            gap: "10px",
          }}
        >
          {currentParent && (
            <button
              type="button"
              style={{
                display: "flex",
                alignItems: "center",
                width: "100%",
                height: "23.988px",
                marginBottom: "10px",
                cursor: isHierarchyNavigationEnabled ? "pointer" : "default",
                paddingTop: "3.996px",
                gap: "10px",
                border: "none",
                background: "transparent",
                paddingLeft: 0,
                paddingRight: 0,
              }}
              onClick={handleBackClick}
            >
              <ChevronBack />
              <span className="text-sm font-semibold text-foreground">{currentParent.name}</span>
            </button>
          )}

          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "10px",
              width: "100%",
              paddingLeft: currentParent ? "13px" : "0px",
            }}
          >
            {categories.map((node) => {
              const isLeaf = node.children === undefined;
              const isSelected = selectedLeafPaths.includes(node.path);
              const isActive = currentPath === node.path;
              const isClickable = isLeaf || isHierarchyNavigationEnabled;

              return (
                <div
                  key={node.path}
                  onClick={() => {
                    if (isLeaf) handleLeafClick(node);
                    else handleNonLeafClick(node);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    height: "24px",
                    paddingTop: "3px",
                    paddingBottom: "3px",
                    cursor: isClickable ? "pointer" : "default",
                    gap: isLeaf ? "22px" : "0px",
                    opacity: isClickable ? 1 : 0.7,
                  }}
                >
                  {isLeaf && (
                    <Checkbox checked={isSelected} onChange={() => handleLeafClick(node)} />
                  )}
                  <span className="flex min-w-0 flex-1 items-center justify-between gap-2">
                    <span className={`${isActive ? "font-medium" : ""} text-sm text-foreground`}>
                      {node.name}
                    </span>
                    {typeof node.count === "number" && (
                      <span className="text-xs text-muted-foreground tabular-nums">
                        {node.count.toLocaleString()}
                      </span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div
        style={{
          width: "100%",
          height: "0.882px",
          borderBottom: "0.882px solid rgba(0, 0, 0, 0.1)",
        }}
      />
    </div>
  );
}
